import {
    createAsyncThunk,
    createSelector,
    createSlice,
} from '@reduxjs/toolkit';

import { chunk } from 'lodash';

import { TRootState } from '../../../../store';
import {
    IFetchRoutes,
    restGetRoute,
    restGetRoutes,
    restPatchRoute,
    restPostRoute,
    restPutRoute,
} from '../../../../services/planning';
import { ISourceSet, TStatus } from '../../../../state/types';
import { removeDuplicatesById } from '../../../../helpers/removeDuplicatesById';
import { getSourceSetModels } from '../../devices';
import { getMappedDictionaries } from '../../../../state/app/dictionaries';
import TranslationHelper from '../../../../helpers/TranslationHelper';
import { ROUTE_STATUS } from '../../../../constants/dictionaries/RouteStatus.';
import moment from 'moment';
import { getOrCreatePool } from '../../../../helpers/createPool';
import { IColor } from '../../../../helpers/ColorPool';
import { UNASSIGNED_ID } from '../tasks/utils/isUnassigned';

const MAX_ITEM_LIMIT = 300;

type IPostRouteBase = Pick<IRoute, 'name' | 'plannedOn' | 'activityCategoryId'>;

export interface IPostRoute extends IPostRouteBase {
    employeeId: number;
    vehicleId: number;
}

export interface IPatchRoute extends Pick<IRoute, 'name'> {
    employeeId: number;
    vehicleId: number;
}

export interface ISelectedRoute {
    id: number;
    sourceSetId: number;
    name: string;
}

export interface ISelectedRouteForTask {
    id: number;
    sourceSetId: number;
    name: string;
    employee?: { id: number; name: string };
    vehicle?: { id: number; name: string };
}

export interface IRoute {
    id: number;
    name: string;
    activityCategoryId: number;
    plannedOn?: string;
    progress?: number;
    status: number;
    realStart?: string;
    realStop?: string;
    employee?: { id: number; name: string };
    vehicle?: { id: number; name: string };
    nodePath: string;
    vehicleId?: number;
}

interface IState {
    routes: { data: IRoute[] | null; status: TStatus };
    taskAssignableRoutes: {
        data: IRoute[] | null;
        status: TStatus;
    };
    mode: null | 'preview' | 'create' | 'edit';
    selectedRouteId: number | null;
    selectedRoute: IRoute | null;
    selectedRoutes: ISelectedRoute[];
    dates: { from: string; to: string };
    activityCategory: number | '';
    routeDetails: { status: TStatus; data: IRoute | null };
}

export const initialState: IState = {
    routes: { data: null, status: 'idle' },
    mode: null,
    selectedRouteId: null,
    selectedRoute: null,
    selectedRoutes: [],
    dates: {
        from: moment(new Date()).format('YYYY-MM-DD'),
        to: moment(new Date()).format('YYYY-MM-DD'),
    },
    taskAssignableRoutes: {
        data: null,
        status: 'idle',
    },
    activityCategory: '',
    routeDetails: {
        status: 'idle',
        data: null,
    },
};

export const fetchRoutes = createAsyncThunk(
    'get:ui/planning/routes-management/routes',
    async (params: IFetchRoutes) => {
        const response = await restGetRoutes(params);

        return response;
    }
);

export const fetchTaskAssignableRoutes = createAsyncThunk(
    'get:ui/planning/routes-management/routesDialog',
    async (params: IFetchRoutes) => {
        const response = await restGetRoutes(params);
        return response;
    }
);

export const fetchRoute = createAsyncThunk(
    'get:ui/planning/routes-management/routes/:id',
    async (id: number) => {
        const response = await restGetRoute(id);

        return response;
    }
);
export const createRoute = createAsyncThunk(
    'post:ui/planning/routes-management/routes',
    async (route: IPostRoute) => {
        const response = await restPostRoute(route);

        return response;
    }
);

export const updateRoute = createAsyncThunk(
    'put:ui/planning/routes-management/routes/{routeId}',
    async (params: { id: number; route: IPatchRoute }) => {
        const response = await restPutRoute(params.id, params.route);

        return response;
    }
);

const processChunk = async (
    routeId: number | undefined,
    ids: number[],
    fetchCall: (
        ids: number[],
        routeId: number | undefined
    ) => Promise<{ id: string }>
) => {
    const tasksChunk = chunk(ids, MAX_ITEM_LIMIT);
    for (let i = 0; i < tasksChunk.length; i++) {
        await fetchCall(tasksChunk[i], routeId);
    }

    return;
};

const handlePatchRouteTasks = async (
    tasksIds: number[],
    routeId: number | undefined
): Promise<{ id: string }> => {
    const tasksIdsChunks = Math.floor(tasksIds.length / MAX_ITEM_LIMIT);
    if (tasksIdsChunks >= 1) {
        await processChunk(routeId, tasksIds, restPatchRoute);
        return Promise.resolve({ id: 'chunked' });
    }
    const response = await restPatchRoute(tasksIds, routeId);
    return response;
};

export const reassignTasks = createAsyncThunk(
    'patch:ui/planning/routes-management/routes',
    async (
        params: { taskIds: number[]; targetRouteId?: number },
        { rejectWithValue }
    ) => {
        try {
            const response = await handlePatchRouteTasks(
                params.taskIds,
                params.targetRouteId
            );
            return response;
        } catch (e: any) {
            throw rejectWithValue({
                status: e.status,
                errorMsg: e.responseJSON?.message,
            });
        }
    }
);

const routes = createSlice({
    name: 'routes',
    initialState,
    reducers: {
        enterRoutePreviewMode(state, action) {
            state.mode = 'preview';
            state.selectedRouteId = action.payload;
        },
        enterRouteCreateMode(state) {
            state.mode = 'create';
        },
        enterRouteEditMode(state) {
            state.mode = 'edit';
        },
        resetRouteMode(state) {
            state.mode = null;
        },
        selectRoutes(state, action) {
            state.selectedRoutes = action.payload;
        },
        selectActivityCategory(state, action) {
            state.activityCategory = action.payload;
        },
        selectDates(state, action) {
            state.dates = action.payload;
        },
        selectRoute(state, action) {
            state.selectedRoute = action.payload;
            state.selectedRouteId = action.payload?.id;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRoutes.pending, (state) => {
                state.routes.status = 'loading';
            })
            .addCase(fetchRoutes.rejected, (state) => {
                state.routes.status = 'error';
            })
            .addCase(fetchRoutes.fulfilled, (state, action) => {
                state.routes.status = 'done';
                state.routes.data = action.payload;
            })
            .addCase(fetchTaskAssignableRoutes.pending, (state) => {
                state.taskAssignableRoutes.status = 'loading';
            })
            .addCase(fetchTaskAssignableRoutes.fulfilled, (state, action) => {
                state.taskAssignableRoutes.status = 'done';
                state.taskAssignableRoutes.data = action.payload;
            })
            .addCase(fetchTaskAssignableRoutes.rejected, (state) => {
                state.taskAssignableRoutes.status = 'error';
            })
            .addCase(fetchRoute.pending, (state) => {
                state.routeDetails.status = 'loading';
            })
            .addCase(fetchRoute.rejected, (state) => {
                state.routeDetails.status = 'error';
            })
            .addCase(fetchRoute.fulfilled, (state, action) => {
                state.routeDetails.status = 'done';
                state.routeDetails.data = action.payload;
            });
    },
});

const getCurrent = (state: TRootState) => state.ui.planning.routes as IState;

export const getRoutesPaneMode = (state: TRootState) => getCurrent(state).mode;
export const getRoutes = (state: TRootState) => getCurrent(state).routes.data;
export const getRoutesStatus = (state: TRootState) =>
    getCurrent(state).routes.status;
export const getTaskAssignableRoutes = (state: TRootState) =>
    getCurrent(state).taskAssignableRoutes.data;
export const getDialogRoutesStatus = (state: TRootState) =>
    getCurrent(state).taskAssignableRoutes.status;
export const getSelectedRouteId = (state: TRootState) =>
    getCurrent(state).selectedRouteId;
export const getSelectedRoute = (state: TRootState) =>
    getCurrent(state).selectedRoute;
export const getRouteDetails = (state: TRootState) =>
    getCurrent(state).routeDetails;
export const getSelectedRoutes = (state: TRootState) =>
    getCurrent(state).selectedRoutes;

export const getSelectedRoutesColors = createSelector(
    [getSelectedRoutes],
    (routes) => {
        const pool = getOrCreatePool('planning');
        const selectedAttributes: string[] = routes.map((route) =>
            String(route.id)
        );
        pool.releaseColors(selectedAttributes);
        return selectedAttributes.reduce(
            (assignedColors: { [key: string]: IColor }, id: string) => {
                assignedColors[id] = pool.assignColor(id);
                return assignedColors;
            },
            {}
        );
    }
);

export const getSelectedCategory = (state: TRootState) =>
    getCurrent(state).activityCategory;
export const getSelectedDates = (state: TRootState) => getCurrent(state).dates;

export const getRoutesSourceSetModel = createSelector(
    [getSourceSetModels],
    (models) => models.planningRoutes || null
);
export const getRoutesAsObject = createSelector(
    [getRoutes],
    (routes: IRoute[] | null) => {
        if (!routes) {
            return {};
        }

        return routes.reduce<Record<string, IRoute>>((acc, route) => {
            const id = route?.id;
            if (id) {
                acc[id] = route;
            }
            return acc;
        }, {});
    }
);

export const isRoutesPaneVisible = createSelector(
    [getRoutesPaneMode],
    (mode) => !!mode
);
export const isRoutesPaneInEditMode = createSelector(
    [getRoutesPaneMode],
    (mode) => mode && mode !== 'preview'
);
export const getRoutesAsSouceSet = createSelector(
    [getRoutes, getMappedDictionaries, getSelectedCategory],
    (routes, dictionaries, currentCategory): ISourceSet | null =>
        routes && {
            id: 'planningRoutes',
            definitionId: 'planningRoutes',
            label: 'Routes',
            attributes: [
                {
                    id: 'placeholder',
                    label: '',
                    type: 'text',
                },
                {
                    id: 'actions',
                    label: 'Actions',
                    type: 'actions',
                },
                {
                    id: 'name',
                    label: 'Name',
                    type: 'text',
                },
                { id: 'vehicleName', label: 'Vehicle name', type: 'text' },
                { id: 'employeeName', label: 'Employee name', type: 'text' },
                {
                    id: 'plannedOn',
                    label: 'Planned on',
                    type: 'date',
                    formatting: { pattern: 'short' },
                },
                {
                    id: 'realStart',
                    label: 'Real start date',
                    type: 'date',
                },
                {
                    id: 'realStop',
                    label: 'Real stop date',
                    type: 'date',
                },
                {
                    id: 'status',
                    label: 'Status',
                    type: 'text',
                    translate: 'wx',
                },
                {
                    id: 'progress',
                    label: 'Progress',
                    type: 'bar',
                },
                {
                    id: 'activityCategory',
                    label: 'Type 1',
                    type: 'text',
                },
                {
                    id: 'nodePath',
                    label: 'Node path',
                    type: 'text',
                },
            ],
            layersAttributes: [],
            _meta: {
                actions: {
                    delete: {
                        label: 'Delete',
                        method: 'DELETE',
                        api: '/rest/api/routes-management/routes/{id}',
                        params: {},
                    },
                },
            },
            entities: removeDuplicatesById([
                {
                    id: UNASSIGNED_ID,
                    name: TranslationHelper.translate('Unassigned'),
                    activityCategoryId: currentCategory,
                },
                ...routes
                    .map((route: IRoute): any => {
                        return {
                            ...route,
                            actions: ['delete', 'preview'],
                            activityCategory:
                                dictionaries['activity-category']?.[
                                    route.activityCategoryId
                                ]?.name,
                            activityCategoryId: route.activityCategoryId,
                            vehicle: route.vehicle,
                            vehicleName: route.vehicle?.name,
                            employee: route.employee,
                            employeeName: route.employee?.name,
                            status: TranslationHelper.translate(
                                ROUTE_STATUS[route.status]?.name
                            ),
                            _meta: {
                                actions: {
                                    delete: {
                                        params: {
                                            id: route.id,
                                        },
                                    },
                                    preview: {
                                        params: {
                                            id: route.id,
                                        },
                                    },
                                },
                            },
                        };
                    })

                    .flat(),
            ]),
        }
);
export const getRoutesAsSourceSetForTask = createSelector(
    [getTaskAssignableRoutes, getMappedDictionaries],
    (routes, dictionaries): ISourceSet | null =>
        routes && {
            id: 'planningRoutes',
            definitionId: 'planningRoutes',
            label: 'Routes',
            attributes: [
                {
                    id: 'placeholder',
                    label: '',
                    type: 'text',
                },
                {
                    id: 'name',
                    label: 'Name',
                    type: 'text',
                },
                { id: 'vehicleName', label: 'Vehicle name', type: 'text' },
                { id: 'employeeName', label: 'Employee name', type: 'text' },
                {
                    id: 'plannedOn',
                    label: 'Planned on',
                    type: 'date',
                    formatting: { pattern: 'short' },
                },
                {
                    id: 'realStart',
                    label: 'Real start date',
                    type: 'date',
                },
                {
                    id: 'realStop',
                    label: 'Real stop date',
                    type: 'date',
                },
                {
                    id: 'status',
                    label: 'Status',
                    type: 'text',
                    translate: 'wx',
                },
                {
                    id: 'progress',
                    label: 'Progress',
                    type: 'bar',
                },
                {
                    id: 'activityCategoryId',
                    label: 'Type 1',
                    type: 'text',
                },
            ],
            layersAttributes: [],
            _meta: {},
            entities: removeDuplicatesById([
                ...routes
                    .map((route: IRoute): any => {
                        return {
                            ...route,
                            activityCategoryId:
                                dictionaries['activity-category']?.[
                                    route.activityCategoryId
                                ]?.name,
                            vehicle: route.vehicle,
                            vehicleName: route.vehicle?.name,
                            employee: route.employee,
                            employeeName: route.employee?.name,
                            status: TranslationHelper.translate(
                                ROUTE_STATUS[route.status]?.name
                            ),
                        };
                    })
                    .flat(),
            ]),
        }
);

export const {
    enterRoutePreviewMode,
    enterRouteCreateMode,
    enterRouteEditMode,
    resetRouteMode,
    selectRoutes,
    selectActivityCategory,
    selectDates,
    selectRoute,
} = routes.actions;

export default routes.reducer;
